import { useState } from "react";
import { Phone, ShieldCheck, Briefcase, Clock, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import BookingModal from "@/components/BookingModal";
import SaintQuizPopup from "@/components/SaintQuizPopup";
import SEOHead from "@/components/SEOHead";
import TmsTherapy from "@/components/sections/TmsTherapy";
import BrighterTomorrowBanner from "@/components/sections/BrighterTomorrowBanner";
import SaintComparisonCallout from "@/components/sections/SaintComparisonCallout";
import EmployerInquiryForm from "@/components/EmployerInquiryForm";
import SaintVideoSection from "@/components/SaintVideoSection";
import { SaintTargetingComparisonSection } from "@/pages/saint/sections/SaintTargetingComparisonSection";
import { SaintOutcomes } from "@/pages/saint/sections/SaintOutcomes";
import { SaintWeek } from "@/pages/saint/sections/SaintWeek";
import { SaintCandidacy } from "@/pages/saint/sections/SaintCandidacy";
import { SaintCost } from "@/pages/saint/sections/SaintCost";
import { SaintStories } from "@/pages/saint/sections/SaintStories";
import { SaintWhyUs } from "@/pages/saint/sections/SaintWhyUs";
import { SaintTravelTeaser } from "@/pages/saint/sections/SaintTravelTeaser";
import { SaintPrivateSuites } from "@/pages/saint/sections/SaintPrivateSuites";
import { SaintFaq } from "@/pages/saint/sections/SaintFaq";
import { SaintConsultationCta } from "@/pages/saint/sections/SaintConsultationCta";
import { SaintArticlesSection } from "@/pages/saint/sections/SaintArticlesSection";
import { SaintDisclaimer } from "@/pages/saint/sections/SaintDisclaimer";
import TwoWaysToGetStarted from "@/components/sections/TwoWaysToGetStarted";
import { RelatedPages } from "@/components/RelatedPages";

const SaintEmployers = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="SAINT® TMS for Employers | Back to Work in 5 Days"
        description="Get employees struggling with depression and anxiety back to work in 5 days with SAINT® TMS. FDA-cleared, fMRI-guided accelerated depression treatment for your workforce."
        canonical="/saint-employers"
        keywords="SAINT TMS employers, employee mental health, depression treatment workplace, accelerated TMS, return to work depression, fMRI-guided TMS, 5-day depression treatment, employee assistance"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            "@id": "https://neurowellnesstms.com/saint#therapy",
            name: "SAINT® Accelerated TMS",
            description:
              "FDA-cleared, fMRI-guided 5-day accelerated transcranial magnetic stimulation for adults with major depressive disorder who have not achieved adequate relief from antidepressants.",
            url: "https://neurowellnesstms.com/saint",
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* ── Employer hero: get employees back to work in 5 days ── */}
        <section className="relative overflow-hidden bg-primary-deep text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary to-primary-deep opacity-95" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
          <div className="pointer-events-none absolute -right-24 -top-20 h-96 w-96 rounded-full bg-accent/20 blur-[90px]" />

          <div className="container-px relative z-10 mx-auto max-w-6xl px-4 py-8 sm:py-12 lg:py-16">
            <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
              {/* Left: employer-focused headline */}
              <div className="text-center lg:text-left">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/20 px-4 py-1.5 text-xs font-bold text-accent shadow-sm sm:text-sm">
                  <Briefcase className="h-4 w-4" />
                  For Employers & HR Teams
                </span>
                <h1 className="mt-4 font-headings text-2xl font-extrabold leading-tight text-white drop-shadow sm:text-3xl md:text-4xl lg:text-[2.65rem]">
                  Get your team back to work in{" "}
                  <span className="text-accent">five days</span>, not weeks
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-white/90 sm:text-base lg:max-w-md">
                  When an employee is out with depression or anxiety, every week
                  of medical leave costs you productivity, coverage, and a
                  teammate. SAINT® is an FDA-cleared, fMRI-guided, five-day
                  treatment that can get them back to full capacity — fast.
                </p>

                {/* Employer value points */}
                <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2.5 lg:justify-start">
                  {[
                    { icon: ShieldCheck, text: "FDA-cleared" },
                    { icon: Clock, text: "5 days vs. 6 weeks" },
                    { icon: TrendingUp, text: "Faster return to work" },
                  ].map((item) => (
                    <div
                      key={item.text}
                      className="flex items-center gap-2 rounded-lg bg-white/5 px-2.5 py-1 backdrop-blur-sm border border-white/10"
                    >
                      <item.icon className="h-4 w-4 text-accent" />
                      <span className="text-xs font-semibold text-white sm:text-sm">
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 lg:hidden">
                  <a
                    href="tel:9548279793"
                    className="inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-95"
                  >
                    <Phone className="h-4 w-4" />
                    Call 954-827-9793
                  </a>
                </div>
              </div>

              {/* Right: eligibility form — the conversion target */}
              <div className="lg:pl-6">
                <EmployerInquiryForm redirectTo="/thank-you-saint-google" />
                <div className="mt-3 hidden justify-center lg:flex">
                  <a
                    href="tel:9548279793"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 underline decoration-accent/50 underline-offset-4 hover:text-white"
                  >
                    <Phone className="h-4 w-4 text-accent" />
                    Prefer to call? 954-827-9793
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── The cost of waiting section (employer-specific) ── */}
        <section className="py-14 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-5xl px-4 text-center">
            <span className="inline-block rounded-full bg-accent/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accent sm:text-sm">
              The Cost of Waiting
            </span>
            <h2 className="mt-5 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
              Depression doesn't just cost your employee — it costs your team
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              A standard course of treatment-resistant depression care means
              medication trials that take six to twelve weeks each, talk therapy
              that builds slowly, and weeks or months of reduced capacity or
              medical leave. Meanwhile, the work piles up, colleagues
              compensate, and your team carries the gap.
            </p>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              SAINT® compresses the entire treatment into five consecutive days.
              Ten short sessions a day, guided by an fMRI of your employee's own
              brain. No anesthesia, no drugs, no downtime after. They fly in,
              they get treated, they come back to work.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="tel:9548279793"
                className="inline-flex items-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-95 sm:text-base"
              >
                <Phone className="h-5 w-5" />
                Talk to our team: 954-827-9793
              </a>
              <a
                href="/saint"
                className="inline-flex items-center gap-2 rounded-xl border border-primary px-6 py-3 text-sm font-bold text-primary hover:bg-primary hover:text-white sm:text-base"
              >
                Learn about SAINT®
              </a>
            </div>
          </div>
        </section>

        {/* ── Why employers choose SAINT ── */}
        <section className="py-14 sm:py-20 bg-muted/30">
          <div className="container-px mx-auto max-w-6xl px-4">
            <div className="text-center">
              <span className="inline-block rounded-full bg-accent/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accent sm:text-sm">
                Why SAINT® for Your Workforce
              </span>
              <h2 className="mt-5 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
                One week. Back to full capacity.
              </h2>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: Clock,
                  title: "5 days instead of 6 weeks",
                  text: "The entire SAINT® protocol — 50 sessions — takes five consecutive days. Compare that to 30–36 daily visits over six to nine weeks for standard TMS, or months of medication trials that may not work.",
                },
                {
                  icon: ShieldCheck,
                  title: "FDA-cleared, fMRI-guided",
                  text: "SAINT® uses a functional MRI of each patient's own brain to identify the exact stimulation target. It's the only FDA-cleared depression treatment that plans care from an image of the individual's brain.",
                },
                {
                  icon: TrendingUp,
                  title: "79% remission in trials",
                  text: "In the double-blind, placebo-controlled trial behind its FDA clearance, 79% of participants reached remission — not just improvement — with an average time to remission of 2.6 days.",
                },
                {
                  icon: Briefcase,
                  title: "Minimal time off work",
                  text: "Five clinic visits instead of 36. For a working professional, that's one arranged week — not two months of daily appointments that make it impossible to stay productive.",
                },
                {
                  icon: ShieldCheck,
                  title: "Non-invasive and drug-free",
                  text: "Nothing enters the bloodstream. No anesthesia, no sedation, no systemic side effects like weight gain or sexual dysfunction. No interaction with existing medications, which don't need to be stopped.",
                },
                {
                  icon: TrendingUp,
                  title: "Private outpatient care",
                  text: "Delivered in a private outpatient clinic with a dedicated care team and weekend availability. Discreet, professional, and designed for people who can't step away from their responsibilities for long.",
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="rounded-2xl border border-border bg-card p-6 text-foreground shadow-soft"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                      <card.icon className="h-5 w-5 text-accent" />
                    </div>
                    <h3 className="font-headings text-lg font-bold text-primary">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {card.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Remaining SAINT content ── */}
        <TmsTherapy />
        <SaintOutcomes />
        <SaintTargetingComparisonSection />
        <SaintVideoSection variant="full" />
        <SaintWeek />
        <SaintCandidacy onQuiz={() => setQuizOpen(true)} />
        <SaintCost />
        <TwoWaysToGetStarted
          heading="Two ways to access SAINT®"
          eyebrow="Access & Availability"
        />
        <SaintComparisonCallout />
        <SaintStories />
        <SaintPrivateSuites />
        <SaintWhyUs />
        <SaintTravelTeaser />
        <SaintFaq />
        <SaintConsultationCta onQuiz={() => setQuizOpen(true)} />
        <BrighterTomorrowBanner />
        <SaintArticlesSection />
        <SaintDisclaimer />
        <RelatedPages
          heading="Related Treatments & Resources"
          links={[
            {
              title: "SAINT® Accelerated TMS",
              href: "/saint",
              description: "The full SAINT® protocol overview",
            },
            {
              title: "SAINT® vs. Standard TMS",
              href: "/learn/saint-vs-traditional-tms",
              description:
                "Key differences in targeting, schedule, and duration",
            },
            {
              title: "Am I a Candidate?",
              href: "/am-i-a-candidate",
              description: "Screening factors — not disqualifiers",
            },
            {
              title: "Cost & Insurance",
              href: "/cost-insurance",
              description: "Financing options and insurance verification",
            },
            {
              title: "Traveling for Treatment",
              href: "/traveling-for-treatment",
              description: "Out-of-state patient accommodations",
            },
            {
              title: "SAINT® vs. NeuroStar",
              href: "/saint-vs-neurostar",
              description: "How SAINT® compares to NeuroStar Advanced Therapy",
            },
          ]}
        />
        <Consultation />
      </main>

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        widgetId="AnQv9vi7iCruuGA7ncnM"
      />
      <SaintQuizPopup open={quizOpen} onOpenChange={setQuizOpen} />

      <Footer />
    </div>
  );
};

export default SaintEmployers;
